import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Typography, TextField, Button, Stack,
    Dialog, DialogContent, DialogActions, Tooltip, IconButton
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { CalendarMonth, Replay, Check, FlashOn, DeleteSweep } from "@mui/icons-material";
import BpmnModeler from "bpmn-js/lib/Modeler";
import { ModdleElement } from "bpmn-js/lib/model/Types";
import Modeling from "bpmn-js/lib/features/modeling/Modeling";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import AnchorConfiguration from "./AnchorConfiguration";
import FrequencyConfiguration from "./FrequencyConfiguration";
import RestrictionsConfiguration from "./RestrictionsConfiguration";
import TimerPreview from "./TimerPreview";
import TimerSummary from "./TimerSummary";
import SingleDateConfiguration from "./SingleDateConfiguration";
import { useSnackbar, useTranslations, useUser } from "hooks";
import { getTranslation } from "common";
import { generateISODurationString, parseISODurationString } from "utils/DateUtils";
import cronstrue from "cronstrue";
import DialogHeader from "components/dialogs/DialogHeader";
import "./TimerConfigurationPanel.css";
import {
    ActiveTab, BaseUnit, CronData, DEFAULT_CRONDATA,
    MONTHS_UI, UNIT_LABELS, WEEK_DAYS,
    buildCronExpression, buildCronExpressionFromChips, computePreviewDates,
    computeUpdatedCronHoursMinutes, parseCronStringToState,
    ScheduledRestriction,
} from "./TimerConfigurationUtils";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";

interface TimerConfigurationPanelProps {
    open: boolean;
    onClose: () => void;
    modeler: BpmnModeler;
    element: ModdleElement;
    onTriggerAutosave?: () => void;
    showDurationOption: boolean;
    showCycleOption: boolean;
    selectedDuration: { months: number; weeks: number; days: number; hours: number; minutes: number };
    setSelectedDuration: (value: { months: number; weeks: number; days: number; hours: number; minutes: number }) => void;
    timeDateValue: Date | null;
    setTimeDateValue: (value: Date | null) => void;
    timerType: string;
    setTimerType: (value: string) => void;
}

const TimerConfigurationPanel: React.FC<TimerConfigurationPanelProps> = ({
    open, onClose, modeler, element, onTriggerAutosave,
    showDurationOption, showCycleOption,
    selectedDuration, setSelectedDuration,
    timeDateValue, setTimeDateValue,
    timerType, setTimerType,
}) => {
    const theme = useTheme();
    const translations = useTranslations();
    const { sendSnack } = useSnackbar();
    const user = useUser();
    const locale = user?.i18n || navigator.language.split("-")[0];

    const [activeTab, setActiveTab] = useState<ActiveTab>("recurring");
    const [useFrequency, setUseFrequency] = useState(true);
    const [baseInterval, setBaseInterval] = useState<number>(1);
    const [baseUnit, setBaseUnit] = useState<BaseUnit>("hours");
    const [useMonthFilter, setUseMonthFilter] = useState(false);
    const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
    const [useDayFilter, setUseDayFilter] = useState(false);
    const [selectedDays, setSelectedDays] = useState<number[]>([]);
    const [useDayOfMonthFilter, setUseDayOfMonthFilter] = useState(false);
    const [selectedDaysOfMonth, setSelectedDaysOfMonth] = useState<number[]>([]);
    const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
    const [startTime, setStartTime] = useState("00:00");
    const [crondata, setCrondata] = useState<CronData>(DEFAULT_CRONDATA);
    const [previewDates, setPreviewDates] = useState<Date[]>([]);

    // ── NEW: restriction chips state ─────────────────────────────────────────
    const [restrictionTime, setRestrictionTime] = useState("00:00");
    const [scheduledRestrictions, setScheduledRestrictions] = useState<ScheduledRestriction[]>([]);

    const minDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

    // ─── Apply parsed cron state to all local state ──────────────────────────

    const setCrondataFromCronstring = useCallback((expression: string) => {
        const s = parseCronStringToState(expression);
        setCrondata(s.crondata);
        setBaseUnit(s.baseUnit);
        setBaseInterval(s.baseInterval);
        setUseFrequency(s.useFrequency);
        setUseMonthFilter(s.useMonthFilter);
        setUseDayFilter(s.useDayFilter);
        setUseDayOfMonthFilter(s.useDayOfMonthFilter);
        setSelectedMonths(s.selectedMonths);
        setSelectedDays(s.selectedDays);
        setSelectedDaysOfMonth(s.selectedDaysOfMonth);
        setStartTime(s.startTime);
        // Restore chips if successfully reconstructed
        if (s.scheduledRestrictions.length > 0) {
            setScheduledRestrictions(s.scheduledRestrictions);
        }
    }, []);

    // ─── Load existing BPMN config ───────────────────────────────────────────

    const loadExistingConfig = useCallback(() => {
        const bo = getBusinessObject(element);
        if (!bo.eventDefinitions?.length) return;
        const eventDef = bo.eventDefinitions[0];
        if (eventDef?.timeCycle?.body) {
            setCrondataFromCronstring(eventDef.timeCycle.body.trim());
        } else if (eventDef?.timeDuration?.body) {
            const parsed = parseISODurationString(eventDef.timeDuration.body);
            if (parsed) setSelectedDuration(parsed);
        } else if (eventDef?.timeDate?.body) {
            const date = new Date(eventDef.timeDate.body);
            if (!isNaN(date.getTime())) setTimeDateValue(date);
        }
    }, [element, setSelectedDuration, setTimeDateValue, setCrondataFromCronstring]);

    // ─── Effects ─────────────────────────────────────────────────────────────

    useEffect(() => {
        if (open) {
            setActiveTab(timerType === "Date" ? "single" : "recurring");
            loadExistingConfig();
        }
    }, [open]);

    // Initialize default timeDateValue for Single Date tab if empty
    useEffect(() => {
        if (open && activeTab === "single" && !timeDateValue) {
            const defaultDate = new Date();
            defaultDate.setMinutes(defaultDate.getMinutes() + 1);
            setTimeDateValue(defaultDate);
        }
    }, [open, activeTab, timeDateValue, setTimeDateValue]);

    useEffect(() => {
        setCrondata(prev => ({ ...prev, months: selectedMonths, daysOfWeek: selectedDays, days: selectedDaysOfMonth }));
    }, [selectedMonths, selectedDays, selectedDaysOfMonth]);

    // Only update cron hours/minutes from startTime when NOT in chips mode
    useEffect(() => {
        if (scheduledRestrictions.length > 0) return;
        const update = computeUpdatedCronHoursMinutes(startTime, baseUnit, baseInterval, useFrequency);
        setCrondata(prev => ({ ...prev, ...update }));
    }, [startTime, baseInterval, baseUnit, useFrequency, scheduledRestrictions.length]);

    useEffect(() => {
        if (!open) return;
        setPreviewDates(computePreviewDates({
            activeTab, startDate, startTime, timeDateValue,
            useMonthFilter, selectedMonths,
            useDayFilter, selectedDays,
            useDayOfMonthFilter, selectedDaysOfMonth,
            baseInterval, baseUnit,
            scheduledRestrictions,
        }));
    }, [open, activeTab, baseInterval, baseUnit, useMonthFilter, selectedMonths, useDayFilter,
        selectedDays, startDate, startTime, timeDateValue, useDayOfMonthFilter, selectedDaysOfMonth,
        scheduledRestrictions]);

    // ─── Toggle helpers ───────────────────────────────────────────────────────

    const toggle = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>) => (val: T) => {
        setter(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);
    };

    const toggleDay = toggle(setSelectedDays);
    const toggleMonth = toggle(setSelectedMonths);
    const toggleDayOfMonth = toggle(setSelectedDaysOfMonth);

    // ─── isDirty Calculation ──────────────────────────────────────────────────
    const isDirty = React.useMemo(() => {
        if (!element) return false;
        const bo = getBusinessObject(element);
        const eventDef = bo.eventDefinitions?.[0];
        const initialType = eventDef?.timeCycle ? "Cycle" : eventDef?.timeDuration ? "Duration" : eventDef?.timeDate ? "Date" : "";

        if (activeTab === "single") {
            const prospectiveDate = timeDateValue && !isNaN(new Date(timeDateValue).getTime()) ? new Date(timeDateValue).toISOString() : "";
            const initialDate = eventDef?.timeDate?.body?.trim() || "";
            return prospectiveDate !== initialDate || initialType !== "Date";
        } else {
            // Only dirty if chips were added/removed (or if switching to cycle from another type)
            if (showCycleOption) {
                // Return explicitly false if no chips are generated, disabling the save button.
                if (scheduledRestrictions.length === 0) return false;

                // If we didn't start with Cycle, and we are saving a Cycle, it's dirty
                if (initialType !== "Cycle" && initialType !== "") return true;

                const initialCronStr = eventDef?.timeCycle?.body?.trim() || "";

                // EXCEPTION: If there is no cycle saved yet at all, any pending change is dirty 
                // so the user can easily save their very first chip from the raw filters.
                if (!initialCronStr) {
                    const prospectiveCron = scheduledRestrictions.length > 0
                        ? buildCronExpressionFromChips(scheduledRestrictions, startTime)
                        : buildCronExpression(crondata);
                    // Default frequency produces a cron string, which is different from ""
                    return prospectiveCron !== "";
                }

                // STRICT BUILD: check if the chips have changed.
                // We parse the initial chips to compare
                const parsedState = parseCronStringToState(initialCronStr);
                const initialChips = parsedState.scheduledRestrictions;

                // If lengths differ, it's dirty
                if (scheduledRestrictions.length !== initialChips.length) return true;

                // Compare chip properties
                for (let i = 0; i < scheduledRestrictions.length; i++) {
                    const r1 = scheduledRestrictions[i];
                    const r2 = initialChips[i];
                    if (r1.time !== r2.time) return true;
                    if (r1.days.join(',') !== r2.days.join(',')) return true;
                    if (r1.daysOfMonth.join(',') !== r2.daysOfMonth.join(',')) return true;
                    if (r1.months.join(',') !== r2.months.join(',')) return true;
                    if (r1.useFrequency !== r2.useFrequency) return true;
                    if (r1.baseInterval !== r2.baseInterval) return true;
                    if (r1.baseUnit !== r2.baseUnit) return true;
                }

                return false;
            } else if (showDurationOption) {
                const prospectiveDuration = generateISODurationString(selectedDuration) || "";
                const initialDuration = eventDef?.timeDuration?.body?.trim() || "";
                return prospectiveDuration !== initialDuration || (initialType !== "Duration" && initialType !== "");
            }
        }
        return false;
    }, [activeTab, timeDateValue, scheduledRestrictions, startTime, crondata, selectedDuration, element, showCycleOption, showDurationOption]);

    // ─── Chip handlers ────────────────────────────────────────────────────────

    const handleConfirmRestriction = () => {
        // Require at least one filter or frequency to be active
        const hasDays = useDayFilter && selectedDays.length > 0;
        const hasDom = useDayOfMonthFilter && selectedDaysOfMonth.length > 0;
        const hasMonths = useMonthFilter && selectedMonths.length > 0;
        const hasFreq = useFrequency;

        if (!hasDays && !hasDom && !hasMonths && !hasFreq) {
            sendSnack({
                message: getTranslation(translations, "pm.timer_config.error.no_restriction_selected") || "Seleziona almeno una frequenza, un giorno o un mese",
                type: "warning",
                autoClose: 3000,
            });
            return;
        }

        const newChip: ScheduledRestriction = {
            id: typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random()}`,
            days: useDayFilter ? [...selectedDays] : [],
            daysOfMonth: useDayOfMonthFilter ? [...selectedDaysOfMonth] : [],
            months: useMonthFilter ? [...selectedMonths] : [],
            time: restrictionTime,
            useFrequency: useFrequency,
            baseInterval: baseInterval,
            baseUnit: baseUnit,
        };

        if (scheduledRestrictions.length >= 1) return;

        setScheduledRestrictions(prev => [...prev, newChip]);

        // Keep current selections so the user can perform quick modifications
    };

    const handleDeleteRestriction = (id: string) => {
        setScheduledRestrictions(prev => {
            const next = prev.filter(r => r.id !== id);
            return next;
        });
    };

    const handleGlobalReset = () => {
        // Reset restrictions
        setSelectedMonths([]);
        setSelectedDays([]);
        setSelectedDaysOfMonth([]);
        setUseMonthFilter(false);
        setUseDayFilter(false);
        setUseDayOfMonthFilter(false);

        // Reset chips
        setScheduledRestrictions([]);
        setRestrictionTime("00:00");

        // Reset frequency settings
        setUseFrequency(false);
        setBaseInterval(1);
        setBaseUnit("hours");

        // Reset anchor settings
        setStartTime("00:00");
        setStartDate(new Date().toISOString().split("T")[0]);
    };

    // ─── Save handlers ────────────────────────────────────────────────────────

    const updateEventDef = (key: "timeCycle" | "timeDuration" | "timeDate", body: string) => {
        const moddle: any = modeler.get("moddle");
        const modeling: Modeling = modeler.get("modeling");
        const bo = getBusinessObject(element);
        let eventDefinition = bo.eventDefinitions[0];
        const OTHERS = ["timeCycle", "timeDuration", "timeDate"].filter(k => k !== key);
        OTHERS.forEach(k => delete eventDefinition[k]);

        if (eventDefinition[key]) {
            eventDefinition[key].body = body;
        } else {
            eventDefinition[key] = moddle.create("bpmn:FormalExpression", { body });
        }
        modeling.updateProperties(element, { eventDefinitions: [eventDefinition] });
    };

    const handleSaveCycle = () => {
        // Use chip-based expression if chips are present, otherwise legacy
        const cronstring = scheduledRestrictions.length > 0
            ? buildCronExpressionFromChips(scheduledRestrictions, startTime)
            : buildCronExpression(crondata);

        updateEventDef("timeCycle", cronstring);
        try { modeler.get<any>("modeling").updateLabel(element, cronstrue.toString(cronstring, { locale })); } catch { }
        setTimerType("Cycle");
        sendSnack({ message: getTranslation(translations, "pm.cycle.save_message"), type: "info", autoClose: 5000 });
        onTriggerAutosave?.();
    };

    const handleSaveDuration = () => {
        const isoString = generateISODurationString(selectedDuration);
        if (!isoString || isoString === "P") return;
        updateEventDef("timeDuration", isoString);
        setTimerType("Duration");
        sendSnack({ message: getTranslation(translations, "generic.model_update.caption"), type: "success" });
        onTriggerAutosave?.();
    };

    const handleSaveDate = () => {
        if (!timeDateValue || isNaN(new Date(timeDateValue).getTime())) return;
        updateEventDef("timeDate", new Date(timeDateValue).toISOString());
        setTimerType("Date");
        sendSnack({ message: getTranslation(translations, "generic.model_update.caption"), type: "success" });
        onTriggerAutosave?.();
    };

    const handleConfirm = () => {
        const now = new Date();
        if (activeTab === "single") {
            if (!timeDateValue || isNaN(new Date(timeDateValue).getTime())) {
                sendSnack({ message: getTranslation(translations, "generic.invalid_data.caption"), type: "error" });
                return;
            }
            if (new Date(timeDateValue) < new Date(now.getTime() - 60000)) {
                sendSnack({ message: getTranslation(translations, "pm.timer_config.error.past_date"), type: "warning" });
                return;
            }
            handleSaveDate();
        } else {
            const [year, month, day] = startDate.split("-").map(Number);
            const startD = new Date(year, month - 1, day);
            if (startD < minDate) {
                sendSnack({ message: getTranslation(translations, "pm.timer_config.error.past_start_date"), type: "warning" });
                return;
            }
            if (showCycleOption) handleSaveCycle();
            else if (showDurationOption) handleSaveDuration();
        }
        onClose();
    };

    // ─── Style constants ──────────────────────────────────────────────────────

    const primary = "#54bdc1";
    const primaryLight = alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.14);
    const secondary = theme.palette.text.primary;
    const borderColor = theme.palette.divider;
    const panelBg = theme.palette.background.default;

    const tabButtonStyle = (isActive: boolean) => ({
        flex: 1, py: 1, fontSize: "0.8rem", fontWeight: 600, borderRadius: "8px",
        border: "none", cursor: "pointer", display: "flex", alignItems: "center",
        justifyContent: "center", gap: 0.5, transition: "all 0.2s",
        backgroundColor: isActive ? "background.paper" : "transparent",
        color: isActive ? primary : "text.secondary",
        boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
        "&:hover": { color: isActive ? primary : "text.primary" },
    });

    const stepDotStyle = (isActive: boolean) => ({
        position: "absolute" as const, left: "-9px", top: 0,
        width: 16, height: 16, borderRadius: "50%",
        backgroundColor: isActive ? primary : theme.palette.action.disabled,
        border: `2px solid ${theme.palette.background.paper}`,
    });

    // ─── Render ───────────────────────────────────────────────────────────────

    const t = (key: string) => getTranslation(translations, key);

    return (
        <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}
            PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden", backgroundColor: "background.paper", color: "text.primary" } }}
        >
            <DialogHeader sx={{ padding: 2 }} onClose={onClose}>
                <Stack padding={2} direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{
                        width: 28, height: 28, borderRadius: "8px", backgroundColor: primaryLight,
                        color: primary, display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                        <FlashOn sx={{ fontSize: 16 }} />
                    </Box>
                    <Stack direction="column">
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                            {t("pm.propertyeditor.specificproperties.label.timer_type")}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {t("pm.propertyeditor.specificproperties.subtitle.timer_type")}
                        </Typography>
                    </Stack>
                </Stack>
            </DialogHeader>

            <DialogContent sx={{ p: 2, backgroundColor: "background.paper" }}>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, minHeight: { xs: "auto", md: 740 } }}>
                    {/* ── Left panel ── */}
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <Box className="timer-panel-scrollbar" sx={{ flex: 1, overflowY: "auto", p: 2.5 }}>
                            {/* Tab switcher */}
                            <Stack direction="row" sx={{ backgroundColor: "action.hover", p: 0.5, borderRadius: "10px", mb: 6 }}>
                                <Box py={4} component="button" onClick={() => { setActiveTab("recurring"); }} sx={tabButtonStyle(activeTab === "recurring")}>
                                    <Replay /> {t("pm.timer_config.tab.recurring")}
                                </Box>
                                <Box py={4} component="button" onClick={() => { setActiveTab("single"); }} sx={tabButtonStyle(activeTab === "single")}>
                                    <CalendarMonth /> {t("pm.timer_config.tab.single")}
                                </Box>
                            </Stack>

                            {activeTab === "recurring" && (
                                <>
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                                        <Button
                                            size="small"
                                            onClick={handleGlobalReset}
                                            startIcon={<DeleteSweep />}
                                            sx={{
                                                fontSize: "0.75rem",
                                                textTransform: "none",
                                                fontWeight: 600,
                                                color: "text.secondary",
                                                "&:hover": {
                                                    color: primary,
                                                    backgroundColor: primaryLight,
                                                    "& .MuiButton-startIcon": { color: primary }
                                                }
                                            }}
                                        >
                                            {t("generic.button.clear") || "Ripristina configurazioni timer"}
                                        </Button>
                                    </Box>
                                    <Stack spacing={6} className="timer-panel-fade-in">
                                        <FrequencyConfiguration
                                            getTranslation={t}
                                            useFrequency={useFrequency} setUseFrequency={setUseFrequency}
                                            baseInterval={baseInterval} setBaseInterval={setBaseInterval}
                                            baseUnit={baseUnit} setBaseUnit={setBaseUnit}
                                            setUseDayFilter={setUseDayFilter} setUseDayOfMonthFilter={setUseDayOfMonthFilter}
                                            useMonthFilter={useMonthFilter} useDayFilter={useDayFilter}
                                            useDayOfMonthFilter={useDayOfMonthFilter} setUseMonthFilter={setUseMonthFilter}
                                            primaryColor={primary} primaryLightColor={primaryLight}
                                            secondaryColor={secondary} panelBg={panelBg} borderColor={borderColor}
                                            stepDotStyle={stepDotStyle} UNIT_LABELS={UNIT_LABELS(translations)}
                                        />
                                        <RestrictionsConfiguration
                                            useMonthFilter={useMonthFilter} setUseMonthFilter={setUseMonthFilter}
                                            selectedMonths={selectedMonths} toggleMonth={toggleMonth}
                                            useDayFilter={useDayFilter} setUseDayFilter={setUseDayFilter}
                                            selectedDays={selectedDays} toggleDay={toggleDay}
                                            useDayOfMonthFilter={useDayOfMonthFilter} setUseDayOfMonthFilter={setUseDayOfMonthFilter}
                                            selectedDaysOfMonth={selectedDaysOfMonth} toggleDayOfMonth={toggleDayOfMonth}
                                            useFrequency={useFrequency} setUseFrequency={setUseFrequency}
                                            primaryColor={primary} primaryLightColor={primaryLight}
                                            secondaryColor={secondary} panelBg={panelBg} borderColor={borderColor}
                                            stepDotStyle={stepDotStyle} MONTHS_UI={MONTHS_UI} WEEK_DAYS={WEEK_DAYS}
                                            getTranslation={t}
                                            restrictionTime={restrictionTime}
                                            setRestrictionTime={setRestrictionTime}
                                            scheduledRestrictions={scheduledRestrictions}
                                            onConfirmRestriction={handleConfirmRestriction}
                                            onDeleteRestriction={handleDeleteRestriction}
                                        />
                                        <AnchorConfiguration
                                            startDate={startDate} setStartDate={setStartDate}
                                            startTime={startTime} setStartTime={setStartTime}
                                            minDate={minDate} primaryColor={primary} primaryLightColor={primaryLight}
                                            secondaryColor={secondary} borderColor={borderColor}
                                            stepDotStyle={stepDotStyle} getTranslation={t}
                                        />

                                        {showDurationOption && (
                                            <Box sx={{ position: "relative", borderLeft: `2px solid ${borderColor}`, pl: 2 }}
                                                className="timer-panel-fade-in">
                                                <Box sx={stepDotStyle(false)} />
                                                <Typography variant="caption" fontWeight={700} color={secondary}
                                                    sx={{ textTransform: "uppercase", letterSpacing: 1, mb: 1.5, display: "block" }}>
                                                    {t("pm.propertyeditor.specificproperties.label.select_duration")}
                                                </Typography>
                                                <Stack spacing={1.5}>
                                                    {([
                                                        { key: "months", label: t("pm.propertyeditor.specificproperties.repetition_details.duration.label.month") },
                                                        { key: "weeks", label: t("pm.propertyeditor.specificproperties.repetition_details.duration.label.weeks") },
                                                        { key: "days", label: t("pm.propertyeditor.specificproperties.repetition_details.duration.label.days") },
                                                        { key: "hours", label: t("pm.propertyeditor.specificproperties.repetition_details.duration.label.hours") },
                                                        { key: "minutes", label: t("pm.propertyeditor.specificproperties.repetition_details.duration.label.minutes"), max: 60 },
                                                    ] as { key: keyof typeof selectedDuration; label: string; max?: number }[]).map(({ key, label, max }) => (
                                                        <TextField key={key} fullWidth size="small" label={label} type="number"
                                                            inputProps={{ min: 0, ...(max ? { max } : {}) }}
                                                            value={selectedDuration[key]}
                                                            onChange={e => setSelectedDuration({ ...selectedDuration, [key]: Number(e.target.value) })}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}
                                    </Stack>
                                </>
                            )}

                            {activeTab === "single" && (
                                <SingleDateConfiguration
                                    timeDateValue={timeDateValue} setTimeDateValue={setTimeDateValue}
                                    minDate={minDate} primaryColor={primary} primaryLightColor={primaryLight}
                                    secondaryColor={secondary} getTranslation={t}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* ── Right panel ── */}
                    <Box sx={{
                        width: { xs: "100%", md: 280 }, backgroundColor: panelBg,
                        borderTop: { xs: `1px solid ${borderColor}`, md: "none" },
                        borderLeft: { xs: "none", md: `1px solid ${borderColor}` },
                        p: 2, display: "flex", flexDirection: "column"
                    }}>
                        <TimerSummary
                            activeTab={activeTab} useFrequency={useFrequency} baseInterval={baseInterval}
                            baseUnit={baseUnit} startTime={startTime} useMonthFilter={useMonthFilter}
                            selectedMonths={selectedMonths} MONTHS_UI={MONTHS_UI} useDayFilter={useDayFilter}
                            selectedDays={selectedDays} WEEK_DAYS={WEEK_DAYS} useDayOfMonthFilter={useDayOfMonthFilter}
                            selectedDaysOfMonth={selectedDaysOfMonth} primaryColor={primary} secondaryColor={secondary}
                            borderColor={borderColor} UNIT_LABELS={UNIT_LABELS(translations)} getTranslation={t}
                            previewDates={previewDates} locale={locale}
                            scheduledRestrictions={scheduledRestrictions}
                        />
                        <TimerPreview
                            getTranslation={t} previewDates={previewDates}
                            borderColor={borderColor} secondaryColor={secondary}
                            locale={locale}
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, backgroundColor: "background.paper" }}>
                <Button onClick={onClose}>{t("generic.button.cancel")}</Button>
                <FlowentiButton variant="contained" onClick={handleConfirm} disabled={!isDirty} startIcon={<Check />}>
                    {t("pm.button.save")}
                </FlowentiButton>
            </DialogActions>
        </Dialog>
    );
};

export default TimerConfigurationPanel;
